import React, { useEffect, useRef, useState } from 'react';
import {
  useLocation,
  Navigate,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import KnameBackdrop from './KnameBackdrop';
import KnameLoading from './KnameLoading';
import {
  useAuth,
  useCopyToast,
  LoginRequiredModal,
  saveAuthReturnTo,
} from '@repo/common';
import { requestKnameDownload } from '../../api/kname/kname.api';
import {
  renderFrontCard,
  renderBackCard,
} from '../../scripts/utils/kname/renderCard';
import { getResult } from '../../scripts/utils/kname/resultCache';

const RESULT_STARS = [
  { x: '8%', y: '20%', s: 3, o: 0.6 },
  { x: '90%', y: '15%', s: 2, o: 0.5 },
  { x: '94%', y: '70%', s: 2, o: 0.4 },
  { x: '12%', y: '85%', s: 2, o: 0.45 },
];

const KnameResult = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const { copy, copyToast } = useCopyToast();

  const { isLoggedIn } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // 고해상도 blob 생성은 무겁다. 버튼을 누르는 순간(pointerdown)부터 미리 만들어
  // 클릭 핸들러가 navigator.share 를 곧바로 호출할 수 있게 한다.
  // (사이에 긴 await 가 끼면 iOS/Chrome 이 user activation 을 잃고 공유시트를 막는다)
  const hiResRef = useRef(null);

  const queryInput = searchParams.get('z')
    ? {
        zodiacId: searchParams.get('z'),
        backgroundId: searchParams.get('b'),
        eraLabel: searchParams.get('e'),
        gender: searchParams.get('g'),
        mood: searchParams.get('m'),
        retryCount: Number(searchParams.get('r') ?? 0),
        givenName: searchParams.get('n') ?? '',
      }
    : null;

  const source = state?.mood ? state : queryInput;

  useEffect(() => {
    if (!source) return;
    let alive = true;
    setError(null);
    getResult(source, (p) => {
      if (alive) setProgress(p);
    })
      .then((r) => {
        if (alive) setResult(r);
      })
      .catch((e) => {
        console.error('result render failed:', e);
        if (alive) setError(e);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (!result) return;
    if (searchParams.get('z')) return;

    const params = new URLSearchParams({
      z: result.selection.zodiacId,
      b: result.selection.backgroundId,
      e: result.selection.eraLabel,
      g: result.derived.gender,
      m: result.derived.mood,
      r: String(result.derived.retryCount ?? 0),
      ...(result.derived.givenName ? { n: result.derived.givenName } : {}),
    });
    navigate(`/korean-name-generator/result?${params.toString()}`, {
      replace: true,
      state,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  if (!source) return <Navigate replace to='/korean-name-generator/form' />;

  const sourceGivenName = source.givenName || '';

  // ── 에러 화면 ──────────────────────────────────
  if (error) {
    return (
      <div className='relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden bg-[#17111d] px-6 py-16'>
        <KnameBackdrop stars={RESULT_STARS} glow={false} />
        <h1
          className='relative z-10 text-center text-[26px] text-[#f3ede2] md:text-3xl'
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          We couldn&apos;t finish your card.
        </h1>
        <p className='relative z-10 mt-4 max-w-sm text-center text-[14px] leading-relaxed text-[#fff5e694]'>
          Something interrupted the connection while we were drawing it. Please
          try again.
        </p>
        <button
          type='button'
          onClick={() => window.location.reload()}
          className='relative z-10 mt-8 h-12 rounded-full bg-[#d6a24b] px-8 text-[15px] font-semibold text-[#17111d] shadow-[0_10px_30px_-12px_rgba(214,162,75,0.55)] transition-all hover:-translate-y-0.5 hover:brightness-110'
        >
          Try again
        </button>
        <button
          type='button'
          onClick={() => navigate('/korean-name-generator/form')}
          className='relative z-10 mt-5 text-[12px] text-[#fff5e694] underline'
        >
          Start over
        </button>
      </div>
    );
  }

  // ── 로딩 화면 ──────────────────────────────────
  if (!result)
    return <KnameLoading givenName={sourceGivenName} progress={progress} />;

  const givenName = sourceGivenName;

  const shareParams = new URLSearchParams({
    z: result.selection.zodiacId,
    b: result.selection.backgroundId,
    e: result.selection.eraLabel,
    g: result.derived.gender,
    m: result.derived.mood,
    r: String(result.derived.retryCount ?? 0),
    ...(givenName ? { n: givenName } : {}),
  });
  const shareUrl = `${window.location.origin}/korean-name-generator/result?${shareParams}`;

  /** 워터마크 없는 고해상도 카드(앞·뒤 합성) blob 을 만든다. */
  const buildHiResBlob = async () => {
    const [front, back] = await Promise.all([
      renderFrontCard({
        backgroundId: result.selection.backgroundId,
        zodiacId: result.selection.zodiacId,
        hangul: result.selection.name.hangul,
        watermark: false,
      }),
      renderBackCard({
        name: result.selection.name,
        zodiacId: result.selection.zodiacId,
        backgroundId: result.selection.backgroundId,
        watermark: false,
      }),
    ]);
    const combined = document.createElement('canvas');
    combined.width = 1200 * 2 + 32;
    combined.height = 1800;
    const cctx = combined.getContext('2d');
    cctx.fillStyle = '#17111d';
    cctx.fillRect(0, 0, combined.width, combined.height);
    cctx.drawImage(front, 0, 0);
    cctx.drawImage(back, 1232, 0);
    return new Promise((r) => combined.toBlob(r, 'image/png'));
  };

  /** 버튼을 누르는 순간 blob 생성을 시작한다. (중복 호출 방지) */
  const prepareHiRes = () => {
    if (!result || hiResRef.current) return;
    hiResRef.current = buildHiResBlob().catch((e) => {
      hiResRef.current = null;
      throw e;
    });
  };

  /** 데스크톱·공유 미지원 브라우저용 폴백 — 일반 파일 다운로드 */
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownload = async () => {
    if (!isLoggedIn) {
      saveAuthReturnTo(
        `/korean-name-generator/result?${shareParams.toString()}`,
      );
      setShowLogin(true);
      return;
    }
    if (downloading) return;
    setDownloading(true);
    try {
      prepareHiRes();
      const blob = await hiResRef.current;
      const filename = `korean-name-${result.selection.name.romanization}.png`;
      const file = new File([blob], filename, { type: 'image/png' });

      // 서버 보관/집계는 저장 흐름을 막지 않도록 백그라운드로 보낸다.
      requestKnameDownload(blob, result.selection).catch((e) =>
        console.error('kname download log failed:', e),
      );

      // iOS/Android: 공유시트가 열리고 "이미지 저장"을 고르면 사진 앱(앨범)에 저장된다.
      // 웹에는 앨범에 직접 쓰는 API가 없어 이 경로가 유일하다.
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }

      downloadBlob(blob, filename);
    } catch (e) {
      // 사용자가 공유시트를 닫은 경우(AbortError)는 실패가 아니다.
      if (e?.name === 'AbortError') return;
      console.error(e);
      alert('Save failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const dataUrlToFile = async (dataUrl, filename) => {
    const blob = await (await fetch(dataUrl)).blob();
    return new File([blob], filename, { type: 'image/png' });
  };

  const handleShare = () => setShareOpen((o) => !o);

  const shareToSns = async () => {
    setShareOpen(false);
    try {
      const file = await dataUrlToFile(
        result.getCombinedShareUrl(),
        'korean-name.png',
      );
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
        });
        return;
      }
    } catch {
      return;
    }
    handleDownload();
    await copy(shareUrl);
  };

  const copyLink = () => copy(shareUrl);

  return (
    <div className='relative flex min-h-[100svh] flex-col items-center justify-start md:justify-center overflow-hidden bg-[#17111d] px-6 py-16'>
      <KnameBackdrop stars={RESULT_STARS} glow={false} />

      {/* 헤드라인 */}
      {givenName && (
        <p
          className='relative z-10 italic text-[17px] text-[#d9b380]'
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {givenName},
        </p>
      )}
      <h1
        className='relative z-10 mt-2 text-center text-[26px] text-[#f3ede2] md:text-4xl'
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Here is your very own Korean name!
      </h1>

      {/* 카드 — 클릭 시 플립 */}
      <button
        type='button'
        onClick={() => setFlipped((f) => !f)}
        className='relative z-10 mt-10 w-[300px] md:w-[360px] [perspective:1500px]'
        aria-label={flipped ? 'Show card front' : 'Show card back'}
      >
        <div
          className='relative aspect-[2/3] w-full transition-transform duration-700 [transform-style:preserve-3d]'
          style={{ transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)' }}
        >
          {/* 앞면 */}
          <img
            src={result.frontUrl}
            alt='Korean name card front'
            draggable={false}
            className='absolute inset-0 h-full w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] [backface-visibility:hidden]'
          />
          {/* 뒷면 */}
          <img
            src={result.backUrl}
            alt='Korean name card back'
            draggable={false}
            className='absolute inset-0 h-full w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] [backface-visibility:hidden]'
            style={{ transform: 'rotateY(180deg)' }}
          />
        </div>
      </button>

      {/* 액션 */}
      <div className='relative z-10 mt-10 flex w-full flex-col gap-4 md:max-w-[380px] md:flex-row'>
        <button
          type='button'
          onClick={handleDownload}
          onPointerDown={prepareHiRes}
          disabled={downloading}
          className='h-12 w-full rounded-full bg-[#d6a24b] px-8 text-[15px] font-semibold text-[#17111d] shadow-[0_10px_30px_-12px_rgba(214,162,75,0.55)] transition-all hover:-translate-y-0.5 hover:brightness-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0'
        >
          {downloading ? 'Preparing…' : 'Save image'}
        </button>

        <div className='relative w-full'>
          <button
            type='button'
            onClick={handleShare}
            className='h-12 w-full rounded-full border border-[#d6a24b] px-8 text-[15px] text-[#f2d29a] transition-colors hover:border-[#f2d29a] hover:text-[#fff5e6]'
          >
            Share
          </button>

          {shareOpen && (
            <>
              {/* 바깥 클릭 닫기 */}
              <div
                className='fixed inset-0 z-10'
                onClick={() => setShareOpen(false)}
              />
              <div className='absolute bottom-full left-1/2 z-20 mb-3 w-[220px] -translate-x-1/2 rounded-2xl border border-[#4a3f4d] bg-[#221820] p-2 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.6)]'>
                {[
                  { label: 'Share to SNS', action: shareToSns },
                  { label: 'Copy link', action: copyLink },
                ].map((item) => (
                  <button
                    key={item.label}
                    type='button'
                    onClick={item.action}
                    className='w-full rounded-xl px-4 py-3 text-left text-[14px] text-[#f3ede2] transition-colors hover:bg-[#2e2130]'
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {showLogin && (
        <LoginRequiredModal isOpen onDismiss={() => setShowLogin(false)} />
      )}
      {copyToast}
    </div>
  );
};

export default KnameResult;
